import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useProducer } from '../../common/hooks/useProducer';
import { productsAPI, sizeOptionsAPI, pricesAPI, categoriesAPI, productImagesAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Input from '../../common/components/Input';
import Card from '../../common/components/Card';
import Loading from '../../common/components/Loading';
import ImageUpload from '../../common/components/ImageUpload';
import { ArrowLeft, Edit2, Check, X, Image, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import './AddProductScreen.css';

const AddProductScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = useProducer();
  const queryClient = useQueryClient();
  const isEditMode = Boolean(id);

  // Form state
  const [productData, setProductData] = useState({
    name: '',
    description: '',
    categoryId: '',
    isActive: true,
  });

  // State for product sizes - array of size configurations
  const [productSizes, setProductSizes] = useState([]);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [uploadingImages, setUploadingImages] = useState({});

  // Fetch data
  const { data: categoriesData } = useQuery('categories', async () => {
    const response = await categoriesAPI.list();
    return response.data.data;
  });

  const { data: sizeOptionsData, isLoading: loadingSizeOptions } = useQuery('size-options', async () => {
    const response = await sizeOptionsAPI.list();
    return response.data.data;
  });

  const { data: productDetails, isLoading: loadingProduct } = useQuery(
    ['product', id],
    async () => {
      const response = await productsAPI.getById(id);
      return response.data.data;
    },
    { enabled: isEditMode }
  );

  const { data: existingPrices } = useQuery(
    ['prices', id],
    async () => {
      const response = await pricesAPI.list(id);
      return response.data.data;
    },
    { enabled: isEditMode }
  );

  const { data: productImages, refetch: refetchProductImages } = useQuery(
    ['productImages', id],
    async () => {
      const response = await productImagesAPI.listByProduct(id);
      return response.data.data;
    },
    { enabled: isEditMode && !!id }
  );

  // Mutations
  const createProductMutation = useMutation(
    async (data) => {
      const response = await productsAPI.create(data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
      },
    }
  );

  const updateProductMutation = useMutation(
    async ({ id, data }) => {
      const response = await productsAPI.update(id, data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        queryClient.invalidateQueries(['product', id]);
      },
    }
  );

  const createPriceMutation = useMutation(
    async (data) => {
      const response = await pricesAPI.create(data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prices');
      },
    }
  );

  const updatePriceMutation = useMutation(
    async ({ id, data }) => {
      const response = await pricesAPI.update(id, data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prices');
      },
    }
  );

  const deletePriceMutation = useMutation(
    async (priceId) => {
      const response = await pricesAPI.delete(priceId);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prices');
        toast.success('Size removed successfully');
      },
    }
  );

  const uploadProductImageMutation = useMutation(
    ({ sizePriceId, file }) => productImagesAPI.upload(sizePriceId, file),
    {
      onMutate: ({ sizePriceId }) => {
        setUploadingImages(prev => ({ ...prev, [sizePriceId]: true }));
      },
      onSuccess: (response, variables) => {
        toast.success('Image uploaded successfully!');
        refetchProductImages();
      },
      onError: (error, variables) => {
        console.error('Upload error:', error);
        toast.error(error.data?.error || error.message || 'Failed to upload image');
      },
      onSettled: (data, error, variables) => {
        setUploadingImages(prev => ({ ...prev, [variables.sizePriceId]: false }));
      },
    }
  );

  const deleteProductImageMutation = useMutation(
    (sizePriceId) => productImagesAPI.delete(sizePriceId),
    {
      onSuccess: () => {
        toast.success('Image deleted successfully!');
        refetchProductImages();
      },
      onError: (error) => {
        console.error('Delete error:', error);
        toast.error(error.data?.error || 'Failed to delete image');
      },
    }
  );

  // Load existing product data
  useEffect(() => {
    if (productDetails) {
      setProductData({
        name: productDetails.name,
        description: productDetails.description || '',
        categoryId: productDetails.category_id || '',
        isActive: productDetails.is_active,
      });
    }
  }, [productDetails]);

  // Initialize product sizes when size options and existing prices are loaded
  useEffect(() => {
    if (sizeOptionsData) {
      if (isEditMode && existingPrices && existingPrices.length > 0) {
        // Edit mode: load existing prices
        const sizes = existingPrices.map((price) => ({
          id: `existing-${price.id}`,
          priceId: price.id,
          sizeOptionId: price.size_option_id,
          sizeOptionName: price.size_option_name,
          price: price.price,
          description: price.description || '',
        }));
        setProductSizes(sizes);
      } else if (!isEditMode && productSizes.length === 0) {
        // Create mode: default to "Standard" size option
        const standardOption = sizeOptionsData.find(opt => opt.name === 'Standard');
        if (standardOption) {
          setProductSizes([{
            id: 'new-1',
            priceId: null,
            sizeOptionId: standardOption.id,
            sizeOptionName: standardOption.name,
            price: '',
            description: '',
          }]);
        }
      }
    }
  }, [sizeOptionsData, existingPrices, isEditMode, productSizes.length]);

  const handleProductInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddSize = () => {
    const newId = `new-${Date.now()}`;
    // Find the first available size option that hasn't been selected
    const selectedSizeIds = productSizes.map(s => s.sizeOptionId);
    const availableOption = sizeOptionsData.find(opt => !selectedSizeIds.includes(opt.id));

    if (availableOption) {
      setProductSizes([...productSizes, {
        id: newId,
        priceId: null,
        sizeOptionId: availableOption.id,
        sizeOptionName: availableOption.name,
        price: '',
        description: '',
      }]);
    } else {
      toast.error('All available size options have been added');
    }
  };

  const handleRemoveSize = async (sizeId, priceId) => {
    if (priceId) {
      // Existing price - soft delete via API
      if (window.confirm('Are you sure you want to remove this size? This action cannot be undone.')) {
        await deletePriceMutation.mutateAsync(priceId);
        setProductSizes(productSizes.filter(s => s.id !== sizeId));
      }
    } else {
      // New size - just remove from state
      setProductSizes(productSizes.filter(s => s.id !== sizeId));
    }
  };

  const handleSizeChange = (sizeId, field, value) => {
    setProductSizes(productSizes.map(size => {
      if (size.id === sizeId) {
        if (field === 'sizeOptionId') {
          const selectedOption = sizeOptionsData.find(opt => opt.id === parseInt(value));
          return {
            ...size,
            sizeOptionId: parseInt(value),
            sizeOptionName: selectedOption?.name || '',
          };
        }
        return { ...size, [field]: value };
      }
      return size;
    }));
  };

  const handlePriceEdit = (sizeId) => {
    setEditingPriceId(sizeId);
  };

  const handlePriceSave = (sizeId) => {
    setEditingPriceId(null);
  };

  const handlePriceCancel = (sizeId) => {
    setEditingPriceId(null);
  };

  const getImageUrlForPrice = (priceId) => {
    if (!productImages || !priceId) return null;
    const image = productImages.find(img => img.product_size_price_id === priceId);
    return image?.url || null;
  };

  const handleProductImageUpload = (priceId, file) => {
    uploadProductImageMutation.mutate({ sizePriceId: priceId, file });
  };

  const handleProductImageDelete = (priceId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteProductImageMutation.mutate(priceId);
    }
  };

  const getAvailableSizeOptions = (currentSizeId) => {
    const selectedSizeIds = productSizes
      .filter(s => s.sizeOptionId !== currentSizeId)
      .map(s => s.sizeOptionId);
    return sizeOptionsData.filter(opt => !selectedSizeIds.includes(opt.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that at least one size has a price
    const sizesWithPrice = productSizes.filter(s => s.price && parseFloat(s.price) > 0);
    if (sizesWithPrice.length === 0) {
      toast.error('Please add at least one size with a price');
      return;
    }

    try {
      // Step 1: Create or update the product
      let productId = id;
      const productPayload = {
        name: productData.name,
        description: productData.description || '',
        categoryId: productData.categoryId ? parseInt(productData.categoryId, 10) : null,
        isActive: productData.isActive,
      };

      if (isEditMode) {
        await updateProductMutation.mutateAsync({
          id,
          data: productPayload,
        });
        toast.success('Product updated successfully');
      } else {
        const newProduct = await createProductMutation.mutateAsync(productPayload);
        productId = newProduct.id;
        toast.success('Product created successfully');
      }

      // Step 2: Create or update prices for each size with a price
      const pricePromises = sizesWithPrice.map((size) => {
        const priceData = {
          productId: productId,
          sizeOptionId: size.sizeOptionId,
          price: parseFloat(size.price),
          stockQuantity: 0,
          isAvailable: true,
          description: size.description || '',
        };

        if (size.priceId) {
          // Update existing price
          return updatePriceMutation.mutateAsync({
            id: size.priceId,
            data: priceData,
          });
        } else {
          // Create new price
          return createPriceMutation.mutateAsync(priceData);
        }
      });

      await Promise.all(pricePromises);

      // Navigate back to products list
      navigate(`/${profile.url_friendly_name}/products`);
    } catch (error) {
      console.error('Error saving product:', error);

      // Show specific validation errors if available
      if (error.details && Array.isArray(error.details)) {
        error.details.forEach(detail => {
          toast.error(`${detail.field}: ${detail.message}`);
        });
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to save product');
      }
    }
  };

  if (loadingSizeOptions || (isEditMode && loadingProduct)) {
    return <Loading message="Loading..." />;
  }

  return (
    <div className="add-product-screen">
      <div className="screen-header">
        <button className="back-button" onClick={() => navigate(`/${profile.url_friendly_name}/products`)}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1>{isEditMode ? 'Edit Product' : 'Add Product'}</h1>
          <p>{isEditMode ? 'Update product details and pricing' : 'Create a new product'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Section 1: Product Details */}
        <Card className="product-section">
          <div className="section-header">
            <h2>Product Details</h2>
            <p>Basic information about your product</p>
          </div>

          <div className="form-group">
            <Input
              label="Product Name"
              name="name"
              value={productData.name}
              onChange={handleProductInputChange}
              required
              placeholder="e.g., Organic Veg Box"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Category (optional)</label>
            <select
              name="categoryId"
              value={productData.categoryId || ''}
              onChange={handleProductInputChange}
              className="form-select"
            >
              <option value="">No category</option>
              {categoriesData?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={productData.description}
              onChange={handleProductInputChange}
              className="form-textarea"
              rows="4"
              placeholder="Describe your product..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="isActive"
                checked={productData.isActive}
                onChange={handleProductInputChange}
                className="form-checkbox"
              />
              <span>Product is active</span>
            </label>
          </div>

          {!isEditMode && (
            <div className="image-upload-info">
              <Image size={20} />
              <p>You can add images for each product size after creating the product</p>
            </div>
          )}
        </Card>

        {/* Section 2: Size & Pricing */}
        <Card className="product-section">
          <div className="section-header">
            <h2>Size & Pricing</h2>
            <p>Add sizes and set prices for each option</p>
          </div>

          {productSizes.length > 0 && (
            <div className="size-options-list">
              {productSizes.map((size) => (
                <div key={size.id} className="size-option-card">
                  <div className="size-option-details">
                    {editingPriceId === size.id ? (
                      <div className="size-option-edit-mode">
                        <div className="size-option-name-display">{size.sizeOptionName}</div>
                        <div className="size-option-inputs">
                          <input
                            type="text"
                            value={size.description}
                            onChange={(e) =>
                              handleSizeChange(size.id, 'description', e.target.value)
                            }
                            className="size-option-input"
                            placeholder="Description (optional)"
                          />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={size.price}
                            onChange={(e) =>
                              handleSizeChange(size.id, 'price', e.target.value)
                            }
                            className="size-option-input"
                            placeholder="Price"
                          />
                        </div>
                        <div className="size-option-actions">
                          <button
                            type="button"
                            className="icon-btn success"
                            onClick={() => handlePriceSave(size.id)}
                            title="Save"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => handlePriceCancel(size.id)}
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="size-option-view-mode">
                        <div className="size-option-info">
                          <div className="size-option-select-wrapper">
                            <select
                              value={size.sizeOptionId}
                              onChange={(e) => handleSizeChange(size.id, 'sizeOptionId', e.target.value)}
                              className="size-option-select"
                            >
                              {getAvailableSizeOptions(size.sizeOptionId).map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="size-option-meta">
                            {size.description && <span className="size-option-description">{size.description}</span>}
                            <span className="size-option-price">
                              {size.price ? `£${parseFloat(size.price).toFixed(2)}` : 'No price set'}
                            </span>
                          </div>
                        </div>
                        <div className="size-option-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => handlePriceEdit(size.id)}
                            title="Edit price"
                          >
                            <Edit2 size={16} />
                          </button>
                          {productSizes.length > 1 && (
                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={() => handleRemoveSize(size.id, size.priceId)}
                              title="Remove size"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {isEditMode && size.priceId && (
                    <div className="size-option-image">
                      <ImageUpload
                        label={`Image for ${size.sizeOptionName}`}
                        currentImageUrl={getImageUrlForPrice(size.priceId)}
                        onUpload={(file) => handleProductImageUpload(size.priceId, file)}
                        onDelete={() => handleProductImageDelete(size.priceId)}
                        isUploading={uploadingImages[size.priceId] || false}
                        maxSize="512KB"
                        dimensions="Recommended: 1000x1000px"
                        acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
                        maxSizeBytes={524288}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {sizeOptionsData && productSizes.length < sizeOptionsData.length && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAddSize}
              className="add-size-button"
            >
              <Plus size={16} />
              Add Size
            </Button>
          )}
        </Card>

        {/* Form Actions */}
        <div className="form-actions">
          <Button type="button" variant="outline" onClick={() => navigate(`/${profile.url_friendly_name}/products`)}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={
              createProductMutation.isLoading ||
              updateProductMutation.isLoading ||
              createPriceMutation.isLoading ||
              updatePriceMutation.isLoading
            }
          >
            {isEditMode ? 'Update Product' : 'Create Product'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddProductScreen;
